/**
 * Shared utilities for rendering forecast overlays on Recharts charts.
 *
 * All 9 time-series charts use the same pattern:
 *  - Actuals render as solid lines/filled bars (existing behaviour, unchanged).
 *  - Forecast months render as dashed lines / hatched bars with reduced opacity.
 *  - A subtitle reminds the reader that the forecast is a floor, not a prediction.
 *
 * Charts call `mergeWithForecast` to overlay forecast values onto the
 * actuals array, tagging overwritten points with `_isForecast: true`.
 * Then chart-specific renderers use `isForecastPoint` to switch style.
 */
import React from 'react';
import { ReferenceLine } from 'recharts';

// ── Month labels ──────────────────────────────────────────────────────────

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Convert "YYYY-MM" to short month label (e.g. "Jun").
 */
export function monthKeyToLabel(monthKey) {
  const parts = monthKey.split('-');
  const idx = parseInt(parts[1], 10) - 1;
  return SHORT_MONTHS[idx] || monthKey;
}

/**
 * Generate a full month-key axis for the given dashboard period.
 *
 * - 'ytd': Jan through Dec of the current year (12 entries).
 * - 'last_12m': rolling 12 months ending at the current month, plus 3
 *   forecast months (15 entries total).
 * - 'all' / anything else: null (caller uses data-driven extent).
 *
 * Returns an array of "YYYY-MM" strings, or null.
 */
export function generateMonthAxis(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based

  if (period === 'ytd') {
    return Array.from({ length: 12 }, (_, i) =>
      `${year}-${String(i + 1).padStart(2, '0')}`
    );
  }

  if (period === 'last_12m') {
    const result = [];
    // 12 actuals months ending at current month, then 3 forecast months
    for (let offset = -11; offset <= 3; offset++) {
      let m = month + offset;
      let y = year;
      while (m < 0) { m += 12; y--; }
      while (m > 11) { m -= 12; y++; }
      result.push(`${y}-${String(m + 1).padStart(2, '0')}`);
    }
    return result;
  }

  return null;
}

/**
 * Get the current month label (the "today" boundary for the reference line).
 */
export function getTodayMonthLabel() {
  return SHORT_MONTHS[new Date().getMonth()];
}

// ── Actuals / forecast boundary (single source of truth) ─────────────────

/**
 * Compute the actuals-vs-forecast month boundary for portfolio charts.
 *
 * The current month is fully actual: every booking with check_in inside
 * the current calendar month belongs in the solid bar, matching the
 * bookings page and `computeYtdSums`. This aligns the chart boundary
 * with `actuals_cutoff_date(today)` (first of next month, exclusive).
 *
 * Forecast (dashed bars / lines) covers exactly the 3 months after the
 * current month. On 2026-06-07: June is the last actuals month (solid),
 * July/Aug/Sep are forecast (dashed), Oct onward is not shown.
 *
 * Every portfolio chart MUST use this helper instead of deriving the
 * boundary from `new Date()` or a "last month with data" scan. The
 * injectable `refDate` parameter makes the boundary deterministic for
 * testing.
 *
 * @param {Date} [refDate=new Date()] - reference date (injectable for tests)
 * @returns {{ lastActualsMonthIdx: number, firstForecastMonthIdx: number, lastForecastMonthIdx: number, currentMonthKey: string, firstForecastMonthKey: string, lastForecastMonthKey: string }}
 */
export function getActualsForecastBoundary(refDate = new Date()) {
  const year = refDate.getFullYear();
  const month = refDate.getMonth(); // 0-based

  function monthKey(m0, y) {
    const wrapped = ((m0 % 12) + 12) % 12;
    const adjustedYear = y + Math.floor(m0 / 12);
    return `${adjustedYear}-${String(wrapped + 1).padStart(2, '0')}`;
  }

  // Forecast window = THIS month plus the next two (Fan, 2026-10-06: "the 3
  // month forecast (including this month)"). The current month carries its
  // actuals to date AND a forecast remainder; the next two are forecast only.
  return {
    lastActualsMonthIdx: month,           // current month (Oct=9 for an October refDate)
    firstForecastMonthIdx: month,         // the current month's remainder is forecast too
    lastForecastMonthIdx: month + 2,      // window = current + 2 (Dec=11 for October)
    currentMonthKey: monthKey(month, year),
    nextMonthKey: monthKey(month + 1, year),
    firstForecastMonthKey: monthKey(month, year),
    lastForecastMonthKey: monthKey(month + 2, year),
  };
}

/**
 * Check if a data point is a forecast point.
 */
export function isForecastPoint(entry) {
  return entry?._isForecast === true;
}

// ── Data merging ──────────────────────────────────────────────────────────

/**
 * Merge forecast monthly data into an actuals data array.
 *
 * Actuals arrays are typically pre-filled with 12 months (Jan-Dec),
 * with zero/null values for future months. For the 3 months strictly
 * after the current month (current+1 through current+3), this function
 * OVERWRITES the existing entry with forecast values and tags it
 * `_isForecast: true`. The current month is actuals; months beyond
 * current+3 keep their original values (typically zero/null).
 *
 * The returned array always has the same length as the input (Jan-Dec).
 * Months past the forecast window keep their original values (typically
 * zero or null), so the x-axis renders all 12 months while lines/bars
 * naturally stop where data ends.
 *
 * @param {Array} actualsData - existing chart data array (Jan-Dec)
 * @param {Record<string, object>} forecastByMonth - { "2026-06": { revenue_gbp, ... } }
 * @param {function} mapFn - (forecastMonthEntry, monthLabel) => data point fields
 * @param {number} currentYear - the year being displayed
 * @param {Date} [refDate] - reference date for boundary (injectable for tests; default: now)
 * @returns {Array} merged data array (new copy, same length as input)
 */
export function mergeWithForecast(actualsData, forecastByMonth, mapFn, currentYear, refDate) {
  if (!forecastByMonth || Object.keys(forecastByMonth).length === 0) {
    return actualsData;
  }

  const boundary = getActualsForecastBoundary(refDate);
  const thisYear = (refDate || new Date()).getFullYear();
  // Only merge forecast for the current year
  if (currentYear != null && currentYear !== thisYear) {
    return actualsData;
  }

  // Whole months merged here are the window months AFTER the current one.
  // The current month keeps its actuals; its remainder is drawn by
  // markCurrentMonthPartial() on the same bar.
  const forecastStart = boundary.nextMonthKey;
  const forecastEnd = boundary.lastForecastMonthKey;
  const year = currentYear != null ? currentYear : thisYear;

  // Match on the full YYYY-MM key, never on the bare month label: from
  // October the window reaches into next year, and "Jan" (2027) used to
  // overwrite January 2026's actuals with a forecast of 0.
  const merged = actualsData.map((d, i) => {
    const idx = SHORT_MONTHS.indexOf(d.month);
    const mk = `${year}-${String((idx >= 0 ? idx : i) + 1).padStart(2, '0')}`;
    if (mk < forecastStart || mk > forecastEnd) return d;
    const fm = forecastByMonth[mk];
    if (!fm) return d;
    const fields = mapFn(fm, d.month);
    if (!fields) return d;
    return { ...d, _isForecast: true, ...fields };
  });

  return merged;
}

/**
 * Current month on a merged-shape bar chart: actuals to date + a forecast
 * remainder drawn as the dashed top of the SAME bar.
 *
 * `forwardByKey` maps each series key to the part of that series' current
 * month value that is still to come (already included in row[key]). The bar
 * keeps its full height (so labels and totals are unchanged) and
 * ForecastBarShape draws the forward part dashed. Forecast footers count it
 * as part of the forecast window.
 */
export function markCurrentMonthPartial(rows, currentYear, forwardByKey, refDate) {
  const boundary = getActualsForecastBoundary(refDate);
  if (!rows || !forwardByKey) return rows;
  const idx = parseInt(boundary.currentMonthKey.slice(5, 7), 10) - 1;
  if (currentYear != null && String(currentYear) !== boundary.currentMonthKey.slice(0, 4)) return rows;
  return rows.map((row, i) => {
    const rowIdx = SHORT_MONTHS.indexOf(row.month);
    if ((rowIdx >= 0 ? rowIdx : i) !== idx) return row;
    const out = { ...row };
    let any = false;
    for (const [key, fwd] of Object.entries(forwardByKey)) {
      const v = Number(fwd) || 0;
      if (v > 0 && Number(row[key]) > 0) {
        out[`_fwd_${key}`] = Math.min(v, Number(row[key]));
        any = true;
      }
    }
    if (any) out._partialForecast = true;
    return out;
  });
}

// ── Full-axis data builder ────────────────────────────────────────────────

/**
 * Build chart data on a full month axis, filling absent months with zeros.
 *
 * Generates the axis from generateMonthAxis(period), then merges in
 * data from a monthKey-keyed object. Missing months get zero for every
 * declared data key. For period='all', falls back to data-driven extent.
 *
 * @param {string} period - 'ytd', 'last_12m', 'all'
 * @param {Record<string, object>} monthMap - { 'YYYY-MM': { key1: val1, ... } }
 * @param {string[]} dataKeys - field names to fill (default 0 for absent)
 * @returns {Array} sorted array of { monthKey, month, ...dataKeys }
 */
export function buildAxisData(period, monthMap, dataKeys) {
  const axis = generateMonthAxis(period);
  const entries = new Map();
  const keys = axis ?? Object.keys(monthMap ?? {}).sort();
  for (const mk of keys) {
    const entry = { monthKey: mk, month: monthKeyToLabel(mk) };
    for (const key of dataKeys) entry[key] = 0;
    entries.set(mk, entry);
  }
  if (monthMap) {
    for (const [mk, data] of Object.entries(monthMap)) {
      const existing = entries.get(mk);
      if (existing) {
        for (const key of dataKeys) {
          if (data[key] != null) existing[key] = data[key];
        }
      } else if (!axis) {
        const entry = { monthKey: mk, month: monthKeyToLabel(mk) };
        for (const key of dataKeys) entry[key] = data[key] ?? 0;
        entries.set(mk, entry);
      }
    }
  }
  return Array.from(entries.entries())
    .sort(function (a, b) { return a[0].localeCompare(b[0]); })
    .map(function (pair) { return pair[1]; });
}

// ── Reference Line ────────────────────────────────────────────────────────

/**
 * Renders a vertical "Today" reference line on the current month.
 * Use inside a Recharts chart (BarChart, LineChart, ComposedChart).
 */
export function ForecastReferenceLine() {
  const label = getTodayMonthLabel();
  return (
    <ReferenceLine
      x={label}
      stroke="#9CA3AF"
      strokeDasharray="4 4"
      strokeWidth={1}
      label={{
        value: 'Today',
        position: 'top',
        fill: '#9CA3AF',
        fontSize: 11,
        fontWeight: 500,
      }}
    />
  );
}

// ── Forecast caveat text ──────────────────────────────────────────────────

export const FORECAST_CAVEAT = 'Forecast shows confirmed forward bookings only. Read as a floor, not a prediction.';

/**
 * Small subtitle element to render below the chart title.
 */
export function ForecastCaveat({ show }) {
  if (!show) return null;
  return (
    <p className="text-xs text-muted-foreground mt-0.5">{FORECAST_CAVEAT}</p>
  );
}

// ── Recharts custom renderers for forecast styling ────────────────────────

/**
 * Custom Bar shape that renders with dashed stroke and reduced opacity
 * for forecast data points. Keeps solid style for actuals.
 *
 * Usage: <Bar shape={<ForecastBarShape dataKey="UK (GBP)" data={chartData} />} />
 *
 * Note: Recharts passes the bar props to the shape component. We read `index`
 * from these props to look up `_isForecast` in the data array.
 *
 * Handles a NEGATIVE `height` (a diverging/negative-value stacked bar, e.g.
 * the Net Assets chart's Liabilities series): Recharts passes a negative
 * height with `y` at the bar's bottom edge for those. SVG `<rect>` requires
 * a non-negative height, so this normalises to the absolute height and
 * shifts `y` up to the correct top edge -- a no-op for every existing
 * positive-only caller (height >= 0 already, so abs/shift are identity).
 */
export function ForecastBarShape({ data, ...barProps }) {
  const { x, y, width, height, fill, index } = barProps;
  const entry = data?.[index];
  const dataKey = barProps.dataKey ?? barProps.tooltipPayload?.[0]?.dataKey;
  // `<key>_forecast` series are forecast by definition, whatever the row says.
  const forecast = entry?._isForecast || (typeof dataKey === 'string' && dataKey.endsWith('_forecast'));

  if (height == null) return null;
  const rectHeight = Math.abs(height);
  if (rectHeight <= 0) return null;
  const rectY = height < 0 ? y + height : y;

  // Current month on a merged-shape chart: solid actuals at the bottom,
  // dashed remainder on top (markCurrentMonthPartial).
  const fwd = entry?._partialForecast && dataKey != null ? Number(entry[`_fwd_${dataKey}`]) : 0;
  const total = dataKey != null ? Number(entry?.[dataKey]) : 0;
  if (!forecast && fwd > 0 && total > 0 && height > 0) {
    const fwdH = Math.min(rectHeight, rectHeight * (fwd / total));
    return (
      <g style={barProps.cursor ? { cursor: barProps.cursor } : undefined}>
        <rect x={x} y={rectY + fwdH} width={width} height={rectHeight - fwdH} fill={fill} />
        <rect x={x} y={rectY} width={width} height={fwdH} fill={fill} fillOpacity={0.4}
          stroke={fill} strokeDasharray="4 2" strokeWidth={1.5} />
      </g>
    );
  }

  return (
    <rect
      x={x}
      y={rectY}
      width={width}
      height={rectHeight}
      fill={fill}
      fillOpacity={forecast ? 0.4 : 1}
      stroke={forecast ? fill : 'none'}
      strokeDasharray={forecast ? '4 2' : undefined}
      strokeWidth={forecast ? 1.5 : 0}
      rx={0}
      ry={0}
      style={barProps.cursor ? { cursor: barProps.cursor } : undefined}
    />
  );
}

/**
 * Custom dot renderer for Line charts that hides dots on forecast points.
 */
export function forecastDot(baseProps) {
  return (props) => {
    const { cx, cy, payload } = props;
    if (payload?._isForecast || cx == null || cy == null) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={baseProps?.r ?? 4}
        fill={baseProps?.fill || props.stroke}
        stroke={baseProps?.stroke || '#fff'}
        strokeWidth={baseProps?.strokeWidth ?? 2}
      />
    );
  };
}

/**
 * For Line charts: we render two Line elements per series (one solid actuals,
 * one dashed forecast) but Recharts does not natively support per-point dash.
 * Instead, we use a single Line with a custom strokeDasharray driven by
 * splitting actuals vs forecast into separate data keys.
 *
 * The simpler approach: keep a single Line, but use `_forecastKey` fields.
 * For a series "UK", actuals use "UK" and forecast uses "UK_forecast".
 * Both Lines share the same colour; the forecast one has strokeDasharray.
 */

/**
 * Split a merged data array into actuals-only and forecast-only keys.
 *
 * For each series name, sets `seriesName` to null on forecast points
 * and `seriesName_forecast` to null on actuals points. This lets two
 * Line components (solid + dashed) render from the same data array
 * with connectNulls so the dashed line starts where solid ends.
 *
 * @param {Array} data - merged actuals+forecast data array
 * @param {string[]} seriesNames - e.g. ['UK', 'FR', 'All']
 * @returns {Array} transformed data array (new copy)
 */
export function splitForecastSeries(data, seriesNames) {
  if (!data?.length) return data;

  // Find the first forecast index, then the last actuals index before it.
  // The array may have non-forecast entries (zero-filled months beyond
  // the 3-month forecast window) AFTER the forecast block, so scanning
  // backwards for the first non-forecast would land on Dec, not the
  // actual boundary.
  let firstForecastIdx = data.findIndex((d) => d._isForecast);
  let lastActualsIdx = firstForecastIdx > 0 ? firstForecastIdx - 1 : -1;

  return data.map((d, i) => {
    const out = { ...d };
    // Tag the anchor point so LabelList renderers can suppress duplicate labels
    if (i === lastActualsIdx && lastActualsIdx >= 0) {
      out._forecastAnchor = true;
    }
    for (const name of seriesNames) {
      if (d._isForecast) {
        // Forecast point: null out the actuals key, populate forecast key
        out[`${name}_forecast`] = d[name];
        out[name] = null;
      } else if (i === lastActualsIdx) {
        // Last actuals point before forecast: duplicate value into forecast
        // key for visual continuity (dashed line starts where solid ends)
        out[`${name}_forecast`] = d[name];
      } else {
        out[`${name}_forecast`] = null;
      }
    }
    return out;
  });
}

/**
 * Create a LabelList `content` renderer for forecast lines that suppresses
 * the label on the forecast anchor point (the current month where the dashed
 * line originates from the solid actuals endpoint). Prevents double-labelling
 * at the boundary month.
 *
 * @param {Array} data - the chart data array (output of splitForecastSeries)
 * @param {object} opts
 * @param {function} opts.formatter - (value) => string label
 * @param {string} opts.fill - text fill colour
 * @param {number} [opts.fontSize=11]
 * @param {number} [opts.offset=8]
 * @param {string} [opts.position='top'] - 'top' or 'bottom'
 */
export function forecastLabelContent(data, { formatter, fill, fontSize = 11, offset = 8, position = 'top' }) {
  return (props) => {
    const { x, y, value, index } = props;
    if (value == null) return null;
    if (data?.[index]?._forecastAnchor) return null;
    const label = formatter(value);
    const dy = position === 'bottom' ? offset : -offset;
    return (
      <text x={x} y={y + dy} textAnchor="middle" fontSize={fontSize} fill={fill}>
        {label}
      </text>
    );
  };
}

/**
 * LabelList `content` for the stacked TOTAL of a forecast month, drawn once
 * per bar on the topmost forecast segment (lib/groupGrossRevenueChart.js
 * annotateForecastTotals stamps `_forecastTotal` / `_forecastCarrier`).
 * Visually distinct from the bold actual-month total: regular weight and
 * the forecast series colour, so a reader never mistakes a floor for an
 * actual.
 *
 * @param {Array} data - the chart data array the Bars render
 * @param {string} carrierKey - this Bar's dataKey (e.g. 'Portfolio_forecast');
 *   the label renders only on the row whose `_forecastCarrier` matches
 * @param {object} opts
 * @param {function} opts.formatter - (value) => string
 * @param {string} opts.fill
 * @param {number} [opts.fontSize=11]
 * @param {number} [opts.offset=7]
 */
export function forecastTotalLabelContent(data, carrierKey, { formatter, fill, fontSize = 11, offset = 7 }) {
  return (props) => {
    const { x, y, width, index } = props;
    const row = data?.[index];
    if (!row || row._forecastCarrier !== carrierKey || !row._forecastTotal) return null;
    return (
      <text
        x={x + (width || 0) / 2}
        y={y - offset}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={600}
        fill={fill}
        data-testid={`forecast-total-${row.month}`}
      >
        {formatter(row._forecastTotal)}
      </text>
    );
  };
}
